import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { fetchProjectNotes, saveProjectNotes } from '../../hooks/useApi'
import { useZustand } from '../../hooks/useZustand'
import { emitEvent, showMessage, trackEvent } from './legacyBridge'
import { registerProjectNotesFlush } from '../LeftMenuMigration/legacyBridge'
import {
    sanitize,
    plainTextToHtml,
    markdownToHtml,
    htmlToPlain,
} from './htmlTransform'
import { extractGraphContextFromStore } from './extractAnalytics'

const AUTOSAVE_DELAY = 2000

// ─── Hook ────────────────────────────────────────────────────────────

export function useProjectNotes() {
    const [notesHtml, setNotesHtml] = useState('')
    const [saveStatus, setSaveStatus] = useState('saved')
    const [isGeneratingAnalytics, setIsGeneratingAnalytics] = useState(false)
    const [scrollToBottomTrigger, setScrollToBottomTrigger] = useState(0)

    const saveTimerRef = useRef(null)
    const notesHtmlRef = useRef('')
    notesHtmlRef.current = notesHtml

    // Signature of the content currently persisted on the server (set on load
    // and after each successful save). The auto-save effect compares against
    // this so that loading a graph's notes never triggers a save-back — only
    // genuine user edits, which diverge from this value, do.
    const lastSavedHtmlRef = useRef('')

    // Single derived value — no separate state to keep in sync
    const notesPlain = useMemo(() => htmlToPlain(notesHtml), [notesHtml])
    const latestPlainRef = useRef('')
    latestPlainRef.current = notesPlain

    // Subscribe to currentContext so notes reload when user switches graph
    const currentContext = useZustand(state => state.currentContext)

    // Load notes from backend API — re-runs when context changes.
    // Uses a load ID to discard stale responses (race condition guard).
    const loadIdRef = useRef(0)

    useEffect(() => {
        const thisLoadId = ++loadIdRef.current

        const loadNotes = async () => {
            const context = currentContext
            if (!context) return
            // Whatever was added beyond the last loaded/saved content — before or during this
            // fetch (an AI-widget save can land before the context is known) — must survive it.
            const savedAtLoadStart = lastSavedHtmlRef.current
            try {
                const description = await fetchProjectNotes(context)
                // Discard if a newer load has started while we were fetching
                if (loadIdRef.current !== thisLoadId) return
                let loadedHtml = ''
                if (description) {
                    // If the saved content contains HTML tags, use it directly.
                    // Otherwise it's legacy plain text — convert to HTML.
                    const isHtml = /<(?:p|h[1-6]|ul|ol|li|br|hr|b|strong|em|i|blockquote|pre)\b/i.test(
                        description
                    )
                    loadedHtml = isHtml
                        ? sanitize(description)
                        : plainTextToHtml(description)
                }
                lastSavedHtmlRef.current = loadedHtml
                // Append it after the loaded notes. Only growth on top of the saved content counts,
                // so a previous graph's notes never leak into this one.
                const localHtml = notesHtmlRef.current
                let additions =
                    localHtml !== savedAtLoadStart &&
                    localHtml.startsWith(savedAtLoadStart)
                        ? localHtml.slice(savedAtLoadStart.length)
                        : ''
                if (!loadedHtml) additions = additions.replace(/^<hr>/, '')
                const separator =
                    loadedHtml && additions && !additions.startsWith('<hr>')
                        ? '<hr>'
                        : ''
                setNotesHtml(sanitize(loadedHtml + separator + additions))
            } catch (err) {
                if (loadIdRef.current !== thisLoadId) return
                console.warn('[ProjectNotes] Failed to load notes:', err)
            }
        }
        loadNotes()
    }, [currentContext])

    // Auto-save with debounce — depends on notesHtml (single source of truth)
    useEffect(() => {
        // Skip when the content matches what's already persisted (e.g. right
        // after loading a graph's notes) — only user edits should save.
        if (notesHtml === lastSavedHtmlRef.current) {
            setSaveStatus('saved')
            return
        }

        setSaveStatus('unsaved')

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)

        saveTimerRef.current = setTimeout(async () => {
            const context = useZustand.getState().currentContext || ''

            if (!context) {
                setSaveStatus('saved')
                return
            }

            const htmlToSave = notesHtmlRef.current
            setSaveStatus('saving')
            try {
                await saveProjectNotes(context, '', htmlToSave)
                lastSavedHtmlRef.current = htmlToSave
                setSaveStatus('saved')
                emitEvent('submitProjectNotes')
            } catch (err) {
                console.error('[ProjectNotes] Save failed:', err)
                setSaveStatus('error')
                showMessage('Failed to save project notes. Please try again.')
            }
        }, AUTOSAVE_DELAY)

        return () => {
            if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        }
    }, [notesHtml])

    // Register an awaitable flush so destructive reloads (e.g. saving
    // Language Settings) can wait for any pending notes save to actually
    // hit the server. Without this, the user's typed text in the debounce
    // window can be lost when the page reloads.
    useEffect(() => {
        const flush = async () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current)
                saveTimerRef.current = null
            }
            const context = useZustand.getState().currentContext || ''
            if (!context) return
            const htmlToSave = notesHtmlRef.current
            if (htmlToSave === lastSavedHtmlRef.current) return
            await saveProjectNotes(context, '', htmlToSave)
            lastSavedHtmlRef.current = htmlToSave
        }
        return registerProjectNotesFlush(flush)
    }, [])

    // Editor change — only updates notesHtml; plain text is derived
    const handleEditorChange = useCallback((html, _plainText) => {
        setNotesHtml(html)
    }, [])

    // Uses ref to read current HTML — stabilizes callback reference
    const handleAutoGenerateAnalytics = useCallback(async () => {
        setIsGeneratingAnalytics(true)

        const contextString = extractGraphContextFromStore()
        if (!contextString) {
            setIsGeneratingAnalytics(false)
            showMessage(
                'No analytics data available. Please load a graph first.'
            )
            return
        }

        const escaped = contextString
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
        const current = notesHtmlRef.current
        const separator = current ? '<hr>' : ''
        setNotesHtml(sanitize(current + separator + `<pre>${escaped}</pre>`))
        setScrollToBottomTrigger(n => n + 1)

        setIsGeneratingAnalytics(false)
    }, [])

    const handleOpenAI = useCallback(type => {
        const eventName = type === 'outline' ? 'notes outline' : 'notes article'
        const analyticsEvent =
            type === 'outline' ? 'ai_notes_outline' : 'ai_notes_article'

        emitEvent('onNotesAction', {
            source: 'notes',
            action: type === 'outline' ? 'notes_outline' : 'notes_article',
        })
        trackEvent(analyticsEvent)

        useZustand.getState().setForceRequestMode({
            name: eventName,
            useInsightClusters: false,
            customInstruction: latestPlainRef.current || '',
        })
    }, [])

    const handleCopy = useCallback(async () => {
        const text = latestPlainRef.current
        if (!text) return
        await navigator.clipboard.writeText(text)
    }, [])

    const handleDownload = useCallback(() => {
        const text = latestPlainRef.current
        if (!text) return
        const context = useZustand.getState().currentContext || 'notes'
        const filename = `project_${context}_notes.txt`

        const blob = new Blob([text], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }, [])

    const handleGenerateGraph = useCallback(() => {
        const text = latestPlainRef.current
        if (!text) {
            showMessage('Add text to the project notes to generate a graph.')
            return
        }

        const currentContextName =
            useZustand.getState().currentContext || 'notes'

        emitEvent('openImportDialog', {
            dialogName: 'textEditor',
            contextName: `${currentContextName}_notes`,
            searchPhrase: text,
        })
        emitEvent('onNotesAction', {
            source: 'projectNotes',
            action: 'generateGraph',
        })
        trackEvent('ai_notes_create_new_graph')
    }, [])

    // Uses ref to read current HTML — stabilizes callback reference
    const handleAddToNotes = useCallback(content => {
        // Always run through markdownToHtml (which escapes HTML entities),
        // then sanitize. This prevents skipping markdown for text that
        // happens to contain angle brackets.
        const htmlContent = markdownToHtml(content)
        const current = notesHtmlRef.current
        const separator = current ? '<hr>' : ''
        setNotesHtml(sanitize(current + separator + htmlContent))
        setScrollToBottomTrigger(n => n + 1)
    }, [])

    return {
        notesHtml,
        notesPlain,
        saveStatus,
        isGeneratingAnalytics,
        scrollToBottomTrigger,
        handleEditorChange,
        handleAutoGenerateAnalytics,
        handleOpenAI,
        handleCopy,
        handleDownload,
        handleGenerateGraph,
        handleAddToNotes,
    }
}
